import { useQuery } from '@tanstack/react-query';
import type { BackendTypes } from '@tf/api';
import { BackendNoAuthClient } from '@tf/api';
import { useGetAuthToken } from '@tf/shared';
import { T } from '@tf/utils';
import constate from 'constate';
import type React from 'react';

import { QueryLoader } from '@/core/providers/QueryLoader';

import { useConfig } from './ConfigProvider';
import { OriginTenantSlugProvider } from './OriginTenantSlugProvider';

type AppMeta = BackendTypes.AppMeta;

type AppEnv = {
	appUrl: string;
	clusterId: string;
	instanceId: string;
};

type EndClientTenant = {
	id: string;
	slug: string;
	nodeId: number;
};

const [AppMetaStateProvider, useAppEnvState, useEndClientTenantState, useOriginTenantIdState] = constate(
	(props: { value: AppMeta }) => {
		return props.value;
	},

	(state): AppEnv => {
		return {
			appUrl: state.appUrl,
			clusterId: state.clusterId,
			instanceId: state.id,
		};
	},

	(state): EndClientTenant => {
		T.assertNotNullish(state.tenantId, 'no tenantId in app meta');
		T.assertNotNullish(state.tenantSlug, 'no tenantSlug in app meta');
		T.assertNotNullish(state.tenantNodeId, 'no tenantNodeId in app meta');

		const tenantNodeId = parseInt(state.tenantNodeId, 10);
		if (Number.isNaN(tenantNodeId)) {
			throw new Error('Invalid tenantNodeId');
		}

		return {
			id: state.tenantId,
			slug: state.tenantSlug,
			nodeId: tenantNodeId,
		};
	},

	(state) => {
		T.assertNotNullish(state.originTenantId, 'no originTenantId in app meta');
		return state.originTenantId;
	},
);

export const useAppEnv = useAppEnvState;

export const useEndClientTenant = useEndClientTenantState;

export const useOriginTenantId = useOriginTenantIdState;

export const AppMetaProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const config = useConfig();
	const getToken = useGetAuthToken();

	const appMetaQuery = useQuery({
		queryKey: ['app'],
		queryFn: async () => {
			BackendNoAuthClient.init(config.urls.backend);

			const token = await getToken();
			const res = await BackendNoAuthClient.getInstance().appMeta({
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});
			return res.json();
		},
		staleTime: Infinity,
	});

	return (
		<QueryLoader
			query={appMetaQuery}
			errorMessage="Failed to load app meta"
			renderData={(data) => {
				return (
					<AppMetaStateProvider value={data}>
						<OriginTenantSlugProvider originTenantSlug={data.originTenantSlug}>{children}</OriginTenantSlugProvider>
					</AppMetaStateProvider>
				);
			}}
		/>
	);
};
